import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Input, Select } from '@/components';

import { ColorPickerAdvanced } from '../controls/ColorPickerAdvanced';
import { GradientEditor } from '../controls/GradientEditor';
import { StyleToggleButton } from '../controls/StyleToggleButton';
import type { LayoutElementStyle } from '../styleTypes';

interface FillSectionProps {
  style: LayoutElementStyle;
  onChange: (updates: Partial<LayoutElementStyle>) => void;
}

export function FillSection({ style, onChange }: FillSectionProps) {
  const { t } = useTranslation();
  const p = 'tenant.systemModuleBuilder.style.';
  const [fillMode, setFillMode] = useState<'solid' | 'gradient' | 'image'>(
    style.gradient ? 'gradient' : style.backgroundImage ? 'image' : 'solid',
  );

  return (
    <div className="space-y-3">
      {/* Mode Toggle */}
      <div className="flex gap-1">
        {(
          [
            { v: 'solid', label: t(`${p}common.color`) },
            { v: 'gradient', label: t(`${p}fill.modeGradient`) },
            { v: 'image', label: t(`${p}fill.modeImage`) },
          ] as const
        ).map(({ v, label }) => (
          <StyleToggleButton key={v} active={fillMode === v} onClick={() => setFillMode(v)}>
            {label}
          </StyleToggleButton>
        ))}
      </div>

      {/* Solid Color */}
      {fillMode === 'solid' && (
        <ColorPickerAdvanced
          color={style.backgroundColor}
          onChange={(c) => onChange({ backgroundColor: c, gradient: undefined })}
          opacity={style.backgroundOpacity}
          onOpacityChange={(o) => onChange({ backgroundOpacity: o })}
          label={t(`${p}effects.backgroundColor`)}
        />
      )}

      {/* Gradient */}
      {fillMode === 'gradient' && (
        <GradientEditor
          gradient={style.gradient}
          onChange={(g) => onChange({ gradient: g, backgroundColor: undefined })}
        />
      )}

      {/* Background Image */}
      {fillMode === 'image' && (
        <div className="space-y-3">
          <div>
            <label className="block text-[10px] text-gray-400 uppercase tracking-wider mb-1">
              {t(`${p}fill.imageUrl`)}
            </label>
            <Input
              type="text"
              value={style.backgroundImage || ''}
              onChange={(e) => onChange({ backgroundImage: e.target.value || undefined })}
              className="px-2 py-1.5 text-xs"
              placeholder="https://..."
            />
          </div>
          {style.backgroundImage && (
            <>
              <div>
                <label className="block text-[10px] text-gray-400 uppercase tracking-wider mb-1">
                  {t(`${p}common.size`)}
                </label>
                <div className="flex gap-1">
                  {(
                    [
                      { v: 'cover', label: t(`${p}fill.imageSizeCover`) },
                      { v: 'contain', label: t(`${p}fill.imageSizeContain`) },
                      { v: 'auto', label: t(`${p}common.auto`) },
                    ] as const
                  ).map(({ v, label }) => (
                    <StyleToggleButton
                      key={v}
                      active={(style.backgroundSize || 'cover') === v}
                      onClick={() => onChange({ backgroundSize: v })}
                    >
                      {label}
                    </StyleToggleButton>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-[10px] text-gray-400 uppercase tracking-wider mb-1">
                  {t(`${p}fill.positionLabel`)}
                </label>
                <Select
                  value={style.backgroundPosition || 'center'}
                  onChange={(e) => onChange({ backgroundPosition: e.target.value })}
                  className="px-2 py-1.5 text-xs"
                >
                  <option value="center">{t(`${p}common.center`)}</option>
                  <option value="top">{t(`${p}common.top`)}</option>
                  <option value="bottom">{t(`${p}common.bottom`)}</option>
                  <option value="left">{t(`${p}common.left`)}</option>
                  <option value="right">{t(`${p}common.right`)}</option>
                  <option value="top left">{t(`${p}fill.posTopLeft`)}</option>
                  <option value="top right">{t(`${p}fill.posTopRight`)}</option>
                  <option value="bottom left">{t(`${p}fill.posBottomLeft`)}</option>
                  <option value="bottom right">{t(`${p}fill.posBottomRight`)}</option>
                </Select>
              </div>
              <div>
                <label className="block text-[10px] text-gray-400 uppercase tracking-wider mb-1">
                  {t(`${p}fill.repeat`)}
                </label>
                <div className="flex gap-1">
                  {(
                    [
                      { v: 'no-repeat', l: t(`${p}common.no`) },
                      { v: 'repeat', l: t(`${p}common.yes`) },
                      { v: 'repeat-x', l: 'X' },
                      { v: 'repeat-y', l: 'Y' },
                    ] as const
                  ).map(({ v, l }) => (
                    <StyleToggleButton
                      key={v}
                      active={(style.backgroundRepeat || 'no-repeat') === v}
                      onClick={() => onChange({ backgroundRepeat: v })}
                    >
                      {l}
                    </StyleToggleButton>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
